import { create } from 'zustand';
import { api } from '../services/api-client';
import { IExercise } from '../types/workout/exercise';

interface ExercisesState {
  exercises: IExercise[];
  isLoading: boolean;
  error: string | null;
  invalidated: boolean;
  loadExercises: (opts?: { search?: string; limit?: number }) => Promise<void>;
  invalidate: () => void;
  /**
   * Agrega un ejercicio al array en memoria sin refetchear. Util cuando se
   * acaba de crear via quick-create — el nuevo ejercicio queda inmediatamente
   * disponible en el dropdown sin hacer otra request al backend.
   */
  addExercise: (exercise: IExercise) => void;
  getExercisesForDropdown: () => Array<{ id: number; name: string }>;
}

// Track the in-flight load promise so concurrent callers can await it
let currentLoadPromise: Promise<void> | null = null;

export const useExercisesStore = create<ExercisesState>((set, get) => ({
  exercises: [],
  isLoading: false,
  error: null,
  invalidated: true,

  loadExercises: async (opts?: { search?: string; limit?: number }) => {
    const { invalidated, exercises, isLoading } = get();

    // If already loading, wait for the current load to finish instead of skipping
    if (isLoading && currentLoadPromise) {
      await currentLoadPromise;
      return;
    }

    // Use cache if not invalidated and has data (only for initial load without search)
    if (!invalidated && exercises.length > 0 && !opts?.search) return;

    set({ isLoading: true, error: null });

    currentLoadPromise = (async () => {
      try {
        const { data } = await api.exercise.fetchCoachExercises({
          search: opts?.search ?? '',
          limit: opts?.limit ?? undefined,
          page: 1
        });
        set({
          exercises: data?.items ?? [],
          isLoading: false,
          error: null,
          invalidated: false
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Error al cargar ejercicios';
        set({ isLoading: false, error: message });
      } finally {
        currentLoadPromise = null;
      }
    })();

    await currentLoadPromise;
  },

  invalidate: () => set({ invalidated: true }),

  addExercise: (exercise: IExercise) => {
    set((state) => ({
      // El nuevo ejercicio va primero para que sea visible inmediatamente al
      // scrollear el dropdown — el matcher fuzzy ya prioriza exact match por id.
      exercises: [exercise, ...state.exercises]
    }));
  },

  getExercisesForDropdown: () => {
    const { exercises } = get();
    return exercises.map((ex) => ({ id: ex.id, name: ex.name }));
  }
}));
